import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Trophy, X } from 'lucide-react';
import type { EmployeeAwardGrant } from '../../types';
import { launchPayrollConfetti } from '../people/PayrollConfettiBurst';
import './EmployeeAwardPopup.css';

const FILE_AWAY_MS = 680;

interface EmployeeAwardPopupProps {
  grant: EmployeeAwardGrant;
  /** Persist "seen" immediately so the grant never re-queues. */
  onAcknowledge: () => void;
  /** Close the popup after any exit animation completes. */
  onFinish: () => void;
  onViewTrophyCase: () => void;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getPeopleNavTarget(): HTMLElement | null {
  return document.querySelector('[data-emp-award-filing-target]');
}

const EmployeeAwardPopup: React.FC<EmployeeAwardPopupProps> = ({
  grant,
  onAcknowledge,
  onFinish,
  onViewTrophyCase,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const niceBtnRef = useRef<HTMLButtonElement>(null);
  const [phase, setPhase] = useState<'open' | 'filing'>('open');
  const filingRef = useRef(false);
  const acknowledgedRef = useRef(false);

  const acknowledgeOnce = useCallback(() => {
    if (acknowledgedRef.current) return;
    acknowledgedRef.current = true;
    onAcknowledge();
  }, [onAcknowledge]);

  const runFileAway = useCallback(() => {
    if (filingRef.current) return;
    filingRef.current = true;

    const popup = popupRef.current;
    const target = getPeopleNavTarget();

    if (!popup || !target || prefersReducedMotion()) {
      onFinish();
      return;
    }

    const from = popup.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    const scale = Math.min(Math.max(to.width / from.width, 0.08), 0.14);

    popup.style.setProperty('--emp-file-x', `${dx}px`);
    popup.style.setProperty('--emp-file-y', `${dy}px`);
    popup.style.setProperty('--emp-file-scale', String(scale));

    target.classList.add('emp-award-filing-target-pulse');
    window.setTimeout(() => target.classList.remove('emp-award-filing-target-pulse'), 900);

    setPhase('filing');
    window.setTimeout(() => onFinish(), FILE_AWAY_MS);
  }, [onFinish]);

  const handleNice = useCallback(() => {
    acknowledgeOnce();
    const btn = niceBtnRef.current;
    if (btn && !prefersReducedMotion()) {
      const rect = btn.getBoundingClientRect();
      launchPayrollConfetti({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }
    runFileAway();
  }, [acknowledgeOnce, runFileAway]);

  const handleQuickDismiss = useCallback(() => {
    acknowledgeOnce();
    onFinish();
  }, [acknowledgeOnce, onFinish]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase === 'open') handleQuickDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleQuickDismiss, phase]);

  return createPortal(
    <div
      className={`emp-award-overlay ${phase === 'filing' ? 'emp-award-overlay--filing' : ''}`}
      onClick={phase === 'open' ? handleQuickDismiss : undefined}
      role="presentation"
    >
      <div
        ref={popupRef}
        className={`emp-award-popup ${phase === 'filing' ? 'emp-award-popup--filing' : ''}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="emp-award-title"
      >
        {phase === 'open' && (
          <button type="button" className="emp-award-close" onClick={handleQuickDismiss} aria-label="Dismiss">
            <X size={18} />
          </button>
        )}

        <div className="emp-award-hero">
          <h2 id="emp-award-title" className="emp-award-title">
            Achievement Unlocked
          </h2>
        </div>

        <div className="emp-award-badge-wrap">
          <img src={grant.iconUrl} alt="" className="emp-award-badge" />
        </div>

        <div className="emp-award-body">
          <h3 className="emp-award-name">{grant.awardName}</h3>
          <p className="emp-award-desc">{grant.awardDescription}</p>
          {grant.grantNote ? <p className="emp-award-note">"{grant.grantNote}"</p> : null}
        </div>

        <div className="emp-award-actions">
          <Link
            to="/people/achievements"
            className="pm-btn pm-btn-primary emp-award-cta"
            onClick={onViewTrophyCase}
          >
            <Trophy size={15} aria-hidden />
            View my trophy case
          </Link>
          <button
            ref={niceBtnRef}
            type="button"
            className="emp-award-nice-btn"
            onClick={handleNice}
            disabled={phase === 'filing'}
          >
            Nice!
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EmployeeAwardPopup;
